import type { ReactNode } from "react";
import { useInView, useReducedMotion } from "@/lib/motion";

/**
 * Below the hero the page is the app in daylight: its light theme (mobile/src/theme.ts), Nunito, slab cards
 * and buttons with a solid bottom edge (common.tsx Slab), and the green course map (course_world.tsx).
 */
export const DAY = {
  ink: "#1C1A27",
  muted: "#6B6780",
  border: "#E7E3F0",
  tint: "#F4F1FF",
  primary: "#6B4EFF",
  primaryDeep: "#4128B8",
  gold: "#FFC83D",
  goldDeep: "#C9931A",
  ground: "#CFE2B4",
  groundDeep: "#C2D9A3",
  nodeShadow: "#D6CFE6",
};

export function DaySection({ id, label, tone = "white", children, className = "" }: { id?: string; label?: string; tone?: "white" | "tint"; children: ReactNode; className?: string }) {
  return (
    <section id={id} aria-label={label} className={`relative scroll-mt-16 px-4 py-20 sm:px-6 sm:py-28 ${tone === "tint" ? "bg-[#F7F5FF]" : "bg-white"} ${className}`}>
      <div className="relative mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

/** The app's heavy uppercase label. */
export function Label({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <p className={`text-xs font-black tracking-[0.12em] text-[#6B4EFF] uppercase sm:text-[13px] ${className}`}>{children}</p>;
}

export function DayHeading({ eyebrow, title, intro, center }: { eyebrow: string; title: ReactNode; intro?: ReactNode; center?: boolean }) {
  return (
    <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
      <Label>{eyebrow}</Label>
      <h2 className="mt-3 text-[1.9rem] leading-[1.12] font-black tracking-tight text-balance text-[#1C1A27] sm:text-[2.6rem] sm:leading-[1.1]">{title}</h2>
      {intro && <p className="mt-4 text-[17px] leading-relaxed text-pretty text-[#6B6780] sm:text-lg">{intro}</p>}
    </div>
  );
}

/** A card with the app's slab edge: a solid darker bottom instead of a shadow. */
export function Slab({ children, className = "", face = "bg-white", edge = "border-[#E7E3F0]" }: { children: ReactNode; className?: string; face?: string; edge?: string }) {
  return <div className={`rounded-[22px] border-2 border-b-[6px] ${edge} ${face} ${className}`}>{children}</div>;
}

const SLAB_BTN = "inline-flex h-14 items-center justify-center rounded-2xl border-b-[5px] px-8 text-[15px] font-black tracking-[0.06em] uppercase transition active:translate-y-[3px] active:border-b-2";

/** The app's CONTINUE button. */
export function SlabButton({ children, onClick, href, quiet, className = "" }: { children: ReactNode; onClick?: () => void; href?: string; quiet?: boolean; className?: string }) {
  const look = quiet ? "border-2 border-b-[5px] border-[#E7E3F0] bg-white text-[#1C1A27] hover:bg-[#FAF9FF]" : "border-[#4128B8] bg-[#6B4EFF] text-white hover:brightness-110";
  if (href)
    return (
      <a href={href} className={`${SLAB_BTN} ${look} ${className}`}>
        {children}
      </a>
    );
  return (
    <button type="button" onClick={onClick} className={`${SLAB_BTN} ${look} ${className}`}>
      {children}
    </button>
  );
}

/** Night to day: the sky ends at the curved edge of the course map's ground; the map's dotted path starts at its crest. */
export function Dawn() {
  return (
    <div aria-hidden className="relative h-[230px] sm:h-[300px]">
      <svg viewBox="0 0 1000 300" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        <defs>
          <filter id="rim-glow" x="-10%" y="-60%" width="120%" height="220%">
            <feGaussianBlur stdDeviation="14" />
          </filter>
        </defs>
        {/* atmosphere: a soft glow along the rim */}
        <path d="M-50 175 Q500 40 1050 175" fill="none" stroke="#B9A8FF" strokeOpacity="0.7" strokeWidth="26" filter="url(#rim-glow)" />
        <path d="M-50 300 L-50 175 Q500 40 1050 175 L1050 300 Z" fill={DAY.ground} />
        <path d="M-50 175 Q500 40 1050 175" fill="none" stroke="#E9F3DC" strokeWidth="5" vectorEffect="non-scaling-stroke" />
      </svg>
      {/* the ground's darker patches, as on the map (kept round: outside the stretched svg) */}
      <div className="absolute bottom-[34px] left-[9%] h-[34px] w-[26%] rounded-full" style={{ background: DAY.groundDeep }} />
      <div className="absolute bottom-[58px] right-[12%] h-[26px] w-[16%] rounded-full" style={{ background: DAY.groundDeep }} />
      <div className="absolute bottom-0 left-1/2 h-[42%] -translate-x-1/2 border-l-[6px] border-dotted border-white/95" />
    </div>
  );
}

/* ---- The course map, as the app draws it ---- */

const MW = 390;
const MH = 790;
const SPINE = 215;

type MapNode = { x: number; y: number; title: string; kicker?: string; kind: "done" | "now" | "open" };

const MAP: MapNode[] = [
  { x: SPINE, y: 70, title: "Why mycelium carries signals", kind: "done" },
  { x: SPINE, y: 175, title: "Electrical spikes in fungi", kind: "done" },
  { x: SPINE, y: 280, title: "Fungal memristors", kicker: "2025 result", kind: "done" },
  { x: SPINE, y: 385, title: "Reading spikes with an ADC", kind: "done" },
  { x: SPINE, y: 515, title: "Wire a two-input fungal gate", kicker: "Next", kind: "now" },
  { x: 100, y: 650, title: "How slime moulds solve mazes", kicker: "Sideways", kind: "open" },
  { x: 290, y: 650, title: "Reservoir computing", kicker: "Go deeper", kind: "open" },
];

const pct = (v: number, of: number) => `${(v / of) * 100}%`;

function Check() {
  return (
    <svg viewBox="0 0 24 24" className="size-[44%]" fill="none" stroke="#fff" strokeWidth={3.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}
function Play({ size = "42%" }: { size?: string }) {
  return (
    <svg viewBox="0 0 24 24" style={{ width: size, height: size }} className="ml-[6%]" aria-hidden>
      <path d="M7 4.5v15a1 1 0 0 0 1.5.9l12-7.5a1 1 0 0 0 0-1.8l-12-7.5A1 1 0 0 0 7 4.5z" fill="#6B4EFF" />
    </svg>
  );
}

/**
 * The green course map: finished lessons down the spine (purple, checked), the next one (white, pulsing) and the
 * options that fan out from it. `upTo` shows nodes up to that index; omitted, it draws itself in when scrolled to.
 */
export function DayMap({ upTo, className = "" }: { upTo?: number; className?: string }) {
  const [ref, seen] = useInView<HTMLDivElement>("-15% 0px");
  const reduced = useReducedMotion();
  const shown = upTo ?? (seen ? MAP.length : 0);
  const anim = upTo == null && !reduced;
  const now = MAP.find(n => n.kind === "now")!;
  return (
    <div ref={ref} className={`relative w-full overflow-hidden rounded-[28px] ${className}`} style={{ aspectRatio: `${MW} / ${MH}`, background: DAY.ground }}>
      <svg viewBox={`0 0 ${MW} ${MH}`} className="absolute inset-0 h-full w-full" aria-hidden>
        <g fill={DAY.groundDeep}>
          <rect x="-120" y="30" width="200" height="150" rx="36" />
          <circle cx="360" cy="250" r="80" />
          <rect x="-90" y="420" width="190" height="200" rx="60" />
          <circle cx="330" cy="560" r="60" />
          <rect x="250" y="-40" width="180" height="110" rx="30" />
          <circle cx="40" cy="760" r="70" />
        </g>
        {/* the path: white dots down the spine, then out to each option */}
        <path d={`M${SPINE} 70 V${MAP[Math.max(0, Math.min(shown, 5) - 1)]!.y}`} stroke="#fff" strokeOpacity={0.9} strokeWidth={6} strokeDasharray="2 14" strokeLinecap="round" fill="none" />
        {shown > 5 &&
          MAP.slice(5, shown).map(n => (
            <path key={n.title} d={`M${now.x} ${now.y} Q${(now.x + n.x) / 2} ${n.y - 10} ${n.x} ${n.y}`} stroke="#fff" strokeOpacity={0.75} strokeWidth={5} strokeDasharray="2 13" strokeLinecap="round" fill="none" />
          ))}
      </svg>
      {MAP.map((n, i) => {
        const on = i < shown;
        const size = n.kind === "now" ? 68 : n.kind === "done" ? 52 : 46;
        const face = n.kind === "done" ? DAY.primary : "#FFFFFF";
        const drop = n.kind === "done" ? DAY.primaryDeep : DAY.nodeShadow;
        const style = { opacity: on ? 1 : 0, transform: `scale(${on ? 1 : 0.6})`, transition: anim ? `opacity 400ms ease-out ${i * 140}ms, transform 500ms cubic-bezier(.3,1.4,.5,1) ${i * 140}ms` : "none" };
        const node = (
          <div className="relative grid shrink-0 place-items-center rounded-full" style={{ width: size, height: size, background: face, border: n.kind === "open" ? `4px solid ${DAY.primary}` : undefined, boxShadow: `0 6px 0 ${drop}${n.kind === "now" ? ", 0 0 0 9px rgba(255,255,255,0.5)" : ""}` }}>
            {n.kind === "now" && !reduced && <span className="absolute inset-0 rounded-full la-ring-pulse" style={{ animationIterationCount: "infinite", animationDuration: "2.2s", borderColor: "#fff", boxShadow: "none" }} />}
            {n.kind === "done" ? <Check /> : <Play size={n.kind === "now" ? "44%" : "40%"} />}
          </div>
        );
        const pill = (
          <span className={`inline-block rounded-[10px] px-2.5 py-1 text-left leading-tight shadow-[0_3px_0_rgba(0,0,0,0.12)] ${n.kind === "now" ? "bg-[#6B4EFF] text-white" : "bg-white text-[#1C1A27]"}`}>
            {n.kicker && <span className={`block text-[9px] font-black tracking-[0.1em] uppercase sm:text-[10px] ${n.kind === "now" ? "text-white/75" : "text-[#6B4EFF]"}`}>{n.kicker}</span>}
            <span className="block text-[11px] font-extrabold sm:text-[13px]">{n.title}</span>
          </span>
        );
        if (n.kind === "done")
          return (
            <div key={n.title} className="absolute flex -translate-y-1/2 flex-row-reverse items-center gap-2.5" style={{ right: pct(MW - n.x - size / 2, MW), top: pct(n.y, MH), width: pct(n.x + size / 2 - 8, MW), ...style }}>
              {node}
              <div className="flex min-w-0 justify-end">{pill}</div>
            </div>
          );
        return (
          <div key={n.title} className="absolute flex w-[44%] -translate-x-1/2 flex-col items-center gap-3 text-center" style={{ left: pct(n.x, MW), top: pct(n.y - size / 2, MH), ...style }}>
            {node}
            {pill}
          </div>
        );
      })}
    </div>
  );
}
